import React, { useCallback, useEffect, useState } from "react";
import Select, { SingleValue } from "react-select";
import { toast } from "react-toastify";
import { useClient } from "../connect_api";
import { ProductService, MachineService, Product, TrackableItem, ProductMap } from "../api/api_pb";

interface BindingProductAndTackableItemProps {
    isOpen: boolean;
    onClose: () => void;
    productMap: ProductMap | null;
    refreshTable: () => void;
}

const BindingProductAndTackableItemModal: React.FC<BindingProductAndTackableItemProps> = ({ isOpen, onClose, refreshTable, productMap }) => {
    const productClient = useClient(ProductService);
    const machineClient = useClient(MachineService);

    const [products, setProducts] = useState<Product[]>([]);
    const [productCounts, setProductCounts] = useState<Map<string, number>>(new Map());
    const [trackableItems, setTrackableItems] = useState<TrackableItem[]>([]);
    const [selectedProduct, setSelectedProduct] = useState<{ value: string; label: string } | null>(null);
    const [selectedTrackableItem, setSelectedTrackableItem] = useState<{ value: string; label: string } | null>(null);
    const [loading, setLoading] = useState(false);

    const fetchProducts = useCallback(async () => {
        try {
            const response = await productClient.listProducts({});
            if (response.products.length > 0) {
                setProducts(response.products);

                // Fetch trackable item counts for each product
                const counts = new Map<string, number>();
                await Promise.all(
                    response.products.map(async (product) => {
                        try {
                            const itemsResponse = await productClient.listProductTrackableItems({
                                productUuid: product.uuid
                            });
                            counts.set(product.uuid, itemsResponse.trackableItems.length);
                        } catch (error) {
                            console.error(`Failed to fetch count for product ${product.uuid}:`, error);
                            counts.set(product.uuid, 0);
                        }
                    })
                );
                setProductCounts(counts);
            }
        } catch (error) {
            toast.error("Failed to fetch products");
            console.error("Fetch Products Error:", error);
        }
    }, [productClient]);

    useEffect(() => {
        if (isOpen) {
            resetProductsAndTrackableItems();
            fetchProducts();
        }
    }, [isOpen, fetchProducts]);

    const handleProductChange = useCallback(async (selectedOption: SingleValue<{ value: string; label: string }>) => {
        if (!selectedOption) {
            setSelectedProduct(null);
            setTrackableItems([]);
            return;
        }
    
        setSelectedProduct(selectedOption);
        setSelectedTrackableItem(null);
    
        try {
            const response = await productClient.listProductTrackableItems({ productUuid: selectedOption.value });
            if (response.trackableItems.length > 0) {
                setTrackableItems(response.trackableItems.filter(item => (!item.location || item.uuid === productMap?.trackableItem?.uuid)));
            } else {
                setTrackableItems([]);
                toast.warning("No trackable items available for this product.");
            }
        } catch (error) {
            toast.error("Failed to fetch trackable items");
            console.error("Fetch TrackableItems Error:", error);
        }
    }, [productClient, productMap]); 

    useEffect(() => {
        if (isOpen && productMap?.product) {
            // Set default product with proper formatting
            const count = productCounts.get(productMap.product.uuid) || 0;
            const productType = productMap.product.forSale ? 'For Sale' : 'For Lease';
            const defaultProduct = {
                value: productMap.product.uuid,
                label: `${productMap.product.name} - ${productType} (${count} items)`
            };
            setSelectedProduct(defaultProduct);
            handleProductChange(defaultProduct);

            if (productMap?.trackableItem) {
                setSelectedTrackableItem({ value: productMap.trackableItem.uuid, label: productMap.trackableItem.name });
            }
        }
    }, [isOpen, productMap, productCounts, handleProductChange]);

    const resetProductsAndTrackableItems = () => {
        setProducts([]);
        setProductCounts(new Map());
        setTrackableItems([]);
        setSelectedProduct(null);
        setSelectedTrackableItem(null);
    };

    const handleSave = async () => {
        if (!selectedProduct || !selectedTrackableItem) {
            toast.error("Please select both product and trackable item");
            return;
        }

        setLoading(true);
        try {
            await machineClient.updateProductMap({
                productMapUuid: productMap?.uuid,
                trackableItemUuid: selectedTrackableItem.value
            });

            toast.success("Binding product and tackable item successfully!");
            refreshTable();
            resetProductsAndTrackableItems();
            onClose();
        } catch (error) {
            toast.error("Failed to binding product and tackable item");
            console.error("Binding product and tackable item error:", error);
        } finally {
            setLoading(false);
        }
    };

    return isOpen ? (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-[1000]">
            <div className="bg-white p-6 rounded-lg shadow-lg w-96">
                <h2 className="text-lg font-bold mb-4">Binding Product and Tackable Item</h2>

                {/* Product Select */}
                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700">Product</label>
                    <Select
                        options={products.map(p => {
                            const count = productCounts.get(p.uuid) || 0;
                            const productType = p.forSale ? 'For Sale' : 'For Lease';
                            return {
                                value: p.uuid,
                                label: `${p.name} - ${productType} (${count} items)`
                            };
                        })}
                        value={selectedProduct}
                        onChange={handleProductChange}
                        isSearchable
                        placeholder="Please select a product..."
                    />
                </div>

                {/* TrackableItem Select */}
                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700">Trackable Item</label>
                    <Select
                        options={trackableItems.map(t => ({ value: t.uuid, label: t.name }))}
                        value={selectedTrackableItem}
                        onChange={setSelectedTrackableItem}
                        isSearchable
                        placeholder="Please select a trackable item..."
                        isDisabled={!selectedProduct}
                    />
                </div>

                {/* Buttons */}
                <div className="flex justify-end space-x-2">
                    <button className="px-4 py-2 bg-gray-500 text-white rounded" onClick={onClose} disabled={loading}>
                        Cancel
                    </button>
                    <button
                        className={`px-4 py-2 rounded ${selectedProduct && selectedTrackableItem && selectedTrackableItem?.value !== productMap?.trackableItem?.uuid
                                ? "bg-blue-600 text-white"
                                : "bg-gray-400 text-gray-200 cursor-not-allowed"
                            }`}
                        onClick={handleSave}
                        disabled={
                            !selectedProduct ||
                            !selectedTrackableItem ||
                            selectedTrackableItem?.value === productMap?.trackableItem?.uuid ||
                            loading
                        }
                    >
                        {loading ? "Saving..." : "Save"}
                    </button>
                </div>
            </div>
        </div>
    ) : null;
};

export default BindingProductAndTackableItemModal;